const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtu.be",
  "www.youtu.be",
]);

const TIKTOK_HOSTS = new Set([
  "tiktok.com",
  "www.tiktok.com",
  "m.tiktok.com",
  "vm.tiktok.com",
  "vt.tiktok.com",
]);

const DIRECT_VIDEO_EXTENSION = /\.(?:mp4|webm|m4v|ogv|ogg)(?:$|\/)/i;
const SAFE_EMBED_ID = /^[A-Za-z0-9_-]{6,128}$/;
const SAFE_TIKTOK_ID = /^\d{6,32}$/;

function toUrl(value) {
  try {
    const parsed = new URL(String(value || ""));
    return ["http:", "https:"].includes(parsed.protocol) ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeHost(hostname) {
  return String(hostname || "").toLowerCase();
}

export function getYouTubeVideoId(value) {
  const parsed = toUrl(value);
  if (!parsed || !YOUTUBE_HOSTS.has(normalizeHost(parsed.hostname))) return null;

  const host = normalizeHost(parsed.hostname);
  const segments = parsed.pathname.split("/").filter(Boolean);
  const candidate = host.endsWith("youtu.be")
    ? segments[0]
    : ["embed", "shorts", "live"].includes(segments[0])
      ? segments[1]
      : parsed.searchParams.get("v");

  return candidate && SAFE_EMBED_ID.test(candidate) ? candidate : null;
}

export function getTikTokVideoId(value) {
  const parsed = toUrl(value);
  if (!parsed || !TIKTOK_HOSTS.has(normalizeHost(parsed.hostname))) return null;

  const segments = parsed.pathname.split("/").filter(Boolean);
  const videoIndex = segments.findIndex((segment) => segment === "video");
  const candidate = videoIndex >= 0 ? segments[videoIndex + 1] : null;
  return candidate && SAFE_TIKTOK_ID.test(candidate) ? candidate : null;
}

function getTikTokAuthorHandle(value) {
  const parsed = toUrl(value);
  if (!parsed || !TIKTOK_HOSTS.has(normalizeHost(parsed.hostname))) return "";

  const candidate = parsed.pathname.split("/").filter(Boolean).find((segment) => segment.startsWith("@"));
  return /^@[A-Za-z0-9._-]{1,64}$/.test(candidate || "") ? candidate : "";
}

function isDirectVideoUrl(parsed) {
  return DIRECT_VIDEO_EXTENSION.test(parsed.pathname);
}

function toAspectRatio(value) {
  return typeof value === "string" && /^\d+\s*\/\s*\d+$/.test(value)
    ? value
    : "16 / 9";
}

/**
 * Builds a browser-safe media description from an already accepted preview URL.
 * It intentionally returns no provider embed HTML.
 */
export function getMediaEmbed(value, metadata = {}) {
  const parsed = toUrl(value);
  if (!parsed) return null;

  const youTubeId = getYouTubeVideoId(parsed.href);
  if (youTubeId) {
    return {
      provider: "youtube",
      kind: "external-video",
      canonicalUrl: `https://www.youtube.com/watch?v=${youTubeId}`,
      embedId: youTubeId,
      posterUrl: metadata.posterUrl || "",
      title: metadata.title || "",
      authorName: metadata.authorName || "",
      aspectRatio: "16 / 9",
    };
  }

  const tikTokId = getTikTokVideoId(parsed.href);
  if (tikTokId) {
    return {
      provider: "tiktok",
      kind: "external-video",
      canonicalUrl: `${parsed.origin}${parsed.pathname}`,
      embedId: tikTokId,
      posterUrl: metadata.posterUrl || "",
      title: metadata.title || "",
      authorName: metadata.authorName || getTikTokAuthorHandle(parsed.href),
      aspectRatio: toAspectRatio(metadata.aspectRatio || "9 / 16"),
    };
  }

  if (isDirectVideoUrl(parsed)) {
    return {
      provider: "direct-video",
      kind: "direct-video",
      canonicalUrl: parsed.href,
      playbackUrl: parsed.href,
      posterUrl: metadata.posterUrl || "",
      title: metadata.title || "",
      authorName: metadata.authorName || "",
      aspectRatio: toAspectRatio(metadata.aspectRatio),
      captions: Array.isArray(metadata.captions) ? metadata.captions : [],
    };
  }

  return null;
}

export function getMediaEmbedPlaybackUrl(embed, { autoplay = false } = {}) {
  if (!embed || typeof embed !== "object") return "";
  const autoplayParam = autoplay ? "&autoplay=1" : "";

  if (embed.provider === "youtube" && SAFE_EMBED_ID.test(embed.embedId || "")) {
    return `https://www.youtube-nocookie.com/embed/${embed.embedId}?rel=0&playsinline=1${autoplayParam}`;
  }

  if (embed.provider === "tiktok" && SAFE_TIKTOK_ID.test(embed.embedId || "")) {
    return `https://www.tiktok.com/player/v1/${embed.embedId}?controls=1&autoplay=${autoplay ? "1" : "0"}`;
  }

  return embed.provider === "direct-video" ? String(embed.playbackUrl || "") : "";
}

export function getProviderLabel(provider) {
  if (provider === "youtube") return "YouTube";
  if (provider === "tiktok") return "TikTok";
  return "Video";
}
